import { observable, computed, action, runInAction } from 'mobx';
import { asyncAction } from 'mobx-utils';
import autobind from 'autobind-decorator';
import { ScoreService } from 'services';
import useLocalStorage from 'hooks/useLocalStorage';
import { ScoreUtil } from 'utils/scoreUtil';
import { timeUtils } from 'utils/timeUtil';
import WebSocketUtil from 'utils/socketUtil';
import { _E_SPORTS_GAME_STATUS } from 'common/constants/sportsConstants';
import StarcraftModel from './model';
import StarcraftSetModel from './model/set';

@autobind
class StarcraftStore {
  localstorage = new useLocalStorage();
  socket = null;
  isSub = false;

  @observable
  list = [];

  @observable
  loading = false;

  @observable
  selectedGames = [];

  @observable
  selectedTab = 'ALL';

  constructor() {
    const today = timeUtils.todayYmd();

    this.localstorage.getAsyncArrItem('starcraft').then((success) => {
      if (!!success) {
        if (success.length > 0) {
          const deleteFavorite = success.filter((item) => {
            const checkDate = item.date;
            const differDay = timeUtils.checkDifferenceToday(checkDate, today);

            return Math.abs(differDay) < 2;
          });

          if (deleteFavorite.length !== success.length) {
            this.localstorage.setItem(
              'starcraft',
              JSON.stringify(deleteFavorite)
            );
            this.selectedGames = deleteFavorite;
          } else {
            this.selectedGames = success;
          }
        }
      } else {
        this.selectedGames = [];
      }
    });
  }

  connectSocket(topic) {
    this.socket = new WebSocketUtil(topic);

    this.socket.ws.onmessage = (event) => {
      if (event) {
        let data = event.data;

        try {
          if (data === 'OK') {
            this.isSub = true;
          } else {
            let messageData = JSON.parse(data);

            if (messageData) {
              const { TOPIC, MESSAGE } = messageData;

              if (TOPIC.includes('star')) {
                const data = JSON.parse(MESSAGE);
                const copy = [...this.list.toJS()];
                let index = null;

                if (data.hasOwnProperty('gidx')) {
                  index = copy.findIndex((item) => item.id === data.gidx);
                }

                if (TOPIC === `esports.star.games`) {
                  this.list[index].updateGame(data);
                }

                if (TOPIC === 'esports.star.period') {
                  this.list[index].updatePeriod(data);
                }
              }
            }
          }
        } catch (error) {
          console.log('=== socket on message error ===', error);
        }
      }
    };
  }

  /**********************************************************
   * computed
   **********************************************************/
  @computed
  get starcraftList() {
    const list = this.list.toJS();
    const selectedTab = this.selectedTab;
    const filterList =
      selectedTab === 'ALL'
        ? list
        : list.filter((item) => {
            const eSportsGameStatus = _E_SPORTS_GAME_STATUS[item.gstatus];

            if (selectedTab === 'READY') {
              return eSportsGameStatus === 'READY';
            } else if (selectedTab === 'PROGRESS') {
              return eSportsGameStatus === 'IN_PROGRESS';
            } else if (selectedTab === 'FINAL') {
              return (
                eSportsGameStatus === 'FINAL' || eSportsGameStatus === 'CANCEL'
              );
            }
          });

    let deleteSelectedGames = [];

    if (this.selectedGames.length > 0) {
      deleteSelectedGames = filterList.filter((item) =>
        this.selectedGames.every((id) => item.id !== id.key)
      );
    } else {
      deleteSelectedGames = filterList;
    }

    return ScoreUtil.eSportsStarSortList(deleteSelectedGames);
  }

  get selectedList() {
    const list = this.list.toJS();
    let selectedGameList = [];

    if (this.selectedGames.length > 0) {
      selectedGameList = list.filter((item) =>
        this.selectedGames.some((id) => id.key === item.id)
      );
    }

    return ScoreUtil.eSportsStarSortList(selectedGameList);
  }

  @computed
  get starcraftCount() {
    const list = this.list.toJS();

    return {
      all: list.length,
      ready: list.filter(
        (item) => _E_SPORTS_GAME_STATUS[item.gstatus] === 'READY'
      ).length,
      inProgress: list.filter(
        (item) => _E_SPORTS_GAME_STATUS[item.gstatus] === 'IN_PROGRESS'
      ).length,
      final: list.filter(
        (item) =>
          _E_SPORTS_GAME_STATUS[item.gstatus] === 'FINAL' ||
          _E_SPORTS_GAME_STATUS[item.gstatus] === 'CANCEL'
      ).length
    };
  }

  /**********************************************************
   * actions
   **********************************************************/
  @action
  closeSocket() {
    this.socket.close(() => {
      runInAction(() => {
        this.isSub = false;
        this.socket = null;
      });
    });
  }

  @action
  setStarcraftSelectedTab(tab) {
    this.selectedTab = tab;
  }

  @action
  setSelectedGame(isSelected, id, date, gameStatus) {
    const { addKeyData, getAsyncArrItem, removeKeyArr } = this.localstorage;

    if (isSelected) {
      removeKeyArr('starcraft', 'key', id);
    } else {
      addKeyData('starcraft', [
        {
          type: 'gameId',
          key: id,
          date,
          gameStatus
        }
      ]);
    }
    getAsyncArrItem('starcraft').then((success) => {
      runInAction(() => {
        this.selectedGames = success;
      });
    });
  }

  /**********************************************************
   * async actions
   **********************************************************/
  @asyncAction
  *getStarcraft(date) {
    this.loading = true;

    try {
      // TODO: 웹소켓;
      if (!this.isSub) {
        yield this.connectSocket([
          'esports.star.games',
          'esports.star.period'
        ]);
      }

      const result = yield ScoreService.getEsportsTypeGames(
        date,
        'ALL',
        'star'
      );

      if (result) {
        // console.log(result);

        this.list = result.map((item) => {
          const deep = { ...item };

          if (deep.set.length > 0) {
            deep.set = item.set.map((set) => {
              let setDeep = { ...set };

              return new StarcraftSetModel(setDeep);
            });
          }

          deep['id'] = item.gidx;

          return new StarcraftModel(deep);
        });

        this.loading = false;
      }
    } catch (error) {
      console.log(error);
    }
  }
}

export default StarcraftStore;
